import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Index.css";

const FEATURES = [
  { icon: "📊", title: "Smart Reports", desc: "Multi-dimensional financial reports with one-click exports to Excel and PDF." },
  { icon: "💳", title: "Omnichannel Payments", desc: "Cash, cards, UPI, and bank transfers with instant auto-reconciliation." },
  { icon: "⚙️", title: "Flexible Fee Structures", desc: "Create dynamic components, discounts, and late-fee rules per course or branch." },
  { icon: "🔒", title: "Bank-Grade Security", desc: "End-to-end encryption, daily backups, and role-based access control." },
  { icon: "🎓", title: "Student Profiles", desc: "Complete student records with documents, photos, and payment history." },
  { icon: "📱", title: "Works Everywhere", desc: "Fully responsive design — manage fees from any device, anywhere." },
];

const STATS = [
  { value: "500+", label: "Institutions" },
  { value: "1M+",  label: "Students Managed" },
  { value: "99%",  label: "Collection Rate" },
  { value: "24/7", label: "Support" },
];

function Index() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem("studentToken"));
  }, []);

  return (
    <div className="home">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-bg-blob hero-blob-1" />
        <div className="hero-bg-blob hero-blob-2" />

        <div className="hero-inner container">
          <div className="hero-text">
            <span className="hero-badge">🚀 Next-Generation Management</span>
            <h1 className="hero-heading">
              Smart <span className="hero-accent">Fees</span><br />
              Management<br />
              For Institutions
            </h1>
            <p className="hero-sub">
              Empower your institution with an all-in-one platform for secure
              online &amp; offline fees collection. Automate reports, track dues,
              and scale seamlessly.
            </p>
            <div className="hero-btns">
              <Link to="/signup" className="btn-primary">
                Get Started Free
              </Link>
              {isLoggedIn ? (
                <Link to="/student-dashboard" className="btn-outline">
                  My Dashboard →
                </Link>
              ) : (
                <Link to="/login" className="btn-outline">
                  Student Login
                </Link>
              )}
            </div>
            <div className="hero-trust">
              <span>✅ Free to start</span>
              <span>✅ No credit card</span>
              <span>✅ Setup in 2 min</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-card-float hero-card-1">
              <span>💰</span>
              <div>
                <p className="hc-label">Collected Today</p>
                <p className="hc-value">₹84,500</p>
              </div>
            </div>
            <div className="hero-img-wrap">
              <img src="/fee.webp" alt="Fees Management Dashboard"
                className="hero-img"
                onError={(e) => { e.target.style.display = "none"; }} />
              {/* Placeholder when no image */}
              <div className="hero-img-placeholder">
                <div className="hip-inner">
                  <span>💼</span>
                  <h3>Web Mok Dashboard</h3>
                  <p>All-in-One Fees Platform</p>
                  <div className="hip-bars">
                    {[80, 55, 90, 65, 75].map((w, i) => (
                      <div key={i} className="hip-bar">
                        <div className="hip-fill" style={{ width: `${w}%` }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="hero-card-float hero-card-2">
              <span>🎓</span>
              <div>
                <p className="hc-label">Active Students</p>
                <p className="hc-value">1,248</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="stats-section">
        <div className="stats-inner container">
          {STATS.map((s, i) => (
            <div key={i} className="stat-item">
              <h2 className="stat-value">{s.value}</h2>
              <p className="stat-label">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Why Web Mok?</span>
            <h2 className="section-heading">Everything Your Institution Needs</h2>
            <p className="section-sub">
              One beautifully designed platform to handle your entire financial workflow.
            </p>
          </div>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="feature-card">
                <div className="fc-icon">{f.icon}</div>
                <h3 className="fc-title">{f.title}</h3>
                <p className="fc-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Process</span>
            <h2 className="section-heading">Get Started in 3 Simple Steps</h2>
          </div>
          <div className="steps-grid">
            {[
              { step: "01", title: "Register Your Institute", desc: "Create an account and set up your institution profile in minutes." },
              { step: "02", title: "Add Students & Courses", desc: "Import or add student records and configure your fee structures." },
              { step: "03", title: "Collect & Track Fees", desc: "Start collecting fees digitally and get real-time reports automatically." },
            ].map((s, i) => (
              <div key={i} className="step-card">
                <div className="step-number">{s.step}</div>
                <h3 className="step-title">{s.title}</h3>
                <p className="step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Testimonials</span>
            <h2 className="section-heading">Trusted by Institutions</h2>
          </div>
          <div className="testimonials-grid">
            {[
              { name: "Priya Sharma", role: "Principal, DPS Academy", text: "Web Mok reduced our fee collection time by 60%. The reports are incredibly detailed and easy to understand.", avatar: "P" },
              { name: "Rajesh Kumar", role: "Admin, TechVision Institute", text: "The multi-branch support is a game changer. We manage 5 branches from a single dashboard now.", avatar: "R" },
              { name: "Anita Singh", role: "Director, Smart Skills", text: "Students love being able to track their own fee status. The dashboard is clean and intuitive.", avatar: "A" },
            ].map((t, i) => (
              <div key={i} className="testimonial-card">
                <p className="tc-text">"{t.text}"</p>
                <div className="tc-author">
                  <div className="tc-avatar">{t.avatar}</div>
                  <div>
                    <p className="tc-name">{t.name}</p>
                    <p className="tc-role">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="cta-inner container">
          <div className="cta-blob" />
          <h2 className="cta-heading">Ready to Modernize Your Institution?</h2>
          <p className="cta-sub">Join hundreds of schools and colleges that trust Web Mok for seamless fee management.</p>
          <div className="cta-btns">
            <Link to="/signup" className="cta-btn-primary">Start Free Trial</Link>
            <Link to="/admin-login" className="cta-btn-outline">Admin Login</Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Index;